import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api, useFetch } from "../lib/api";
import { useShop } from "../lib/store";
import { chf } from "../lib/format";
import { Seo, Loading, Empty, SectionHead } from "../components/Common";
import { ProductRail } from "../components/ProductCard";

export const useQuote = (extra = {}) => {
  const { cart } = useShop();
  const { lang } = useI18n();
  const [q, setQ] = useState(null);
  const payload = JSON.stringify({ items: cart.map(({ sku, qty }) => ({ sku, qty })), lang, ...extra });
  useEffect(() => {
    const body = JSON.parse(payload);
    if (!body.items.length) { setQ({ lines: [], errors: [], total: 0 }); return; }
    let alive = true;
    api.post("/cart/quote", body).then((r) => alive && setQ(r.data)).catch((e) => { console.error("Quote failed", e); if (alive) setQ(null); });
    return () => { alive = false; };
  }, [payload]);
  return q;
};

export const Totals = ({ q, fmt = chf }) => {
  const { t } = useI18n();
  const row = (l, v, cls = "", tid) => <div className={`flex justify-between gap-4 text-sm py-1.5 ${cls}`} data-testid={tid}><span>{l}</span><span className="whitespace-nowrap">{v}</span></div>;
  return (
    <div data-testid="order-totals">
      {row(t("subtotal"), fmt(q.subtotal), "", "totals-subtotal")}
      {q.bundle_discount > 0 && row(`${t("bundle_discount")} (${q.bundles.map((b) => b.name).join(", ")})`, `− ${fmt(q.bundle_discount)}`, "text-[color:var(--hw-green)]", "totals-bundle")}
      {q.coupon_discount > 0 && row(`${t("discount")} (${q.coupon})`, `− ${fmt(q.coupon_discount)}`, "text-[color:var(--hw-green)]", "totals-coupon")}
      {row(t("shipping"), fmt(q.shipping), "", "totals-shipping")}
      <div className="border-t border-[color:var(--hw-line)] mt-2 pt-3 flex justify-between font-semibold text-lg" data-testid="totals-total"><span>{t("total")}</span><span>{fmt(q.total)}</span></div>
      {q.vat_rate > 0 && <p className="text-xs text-[color:var(--hw-muted)] text-right" data-testid="totals-vat">{t("vat_included", { r: q.vat_rate })}: {fmt(q.vat)}</p>}
    </div>
  );
};

export default function Cart() {
  const { t, lang, val } = useI18n();
  const { cart, setQty, replaceSku } = useShop();
  const [coupon, setCoupon] = useState(() => sessionStorage.getItem("hw_coupon") || "");
  const [applied, setApplied] = useState(coupon);
  const [method, setMethod] = useState(() => sessionStorage.getItem("hw_ship") || "");
  const q = useQuote({ coupon: applied || null, shipping_method: method || null });
  const { data: sugg } = useFetch(`/products?sort=bestsellers&limit=8&lang=${lang}`);
  const [variantsBySlug, setVariants] = useState({});
  useEffect(() => { sessionStorage.setItem("hw_coupon", applied); }, [applied]);
  useEffect(() => { if (q?.shipping_method) sessionStorage.setItem("hw_ship", q.shipping_method); }, [q]);
  const requested = useRef(new Set());
  useEffect(() => {
    (q?.lines || []).forEach(({ product_slug: slug }) => {
      if (requested.current.has(slug)) return;
      requested.current.add(slug);
      api.get(`/products/${slug}?lang=${lang}`).then((r) => setVariants((m) => ({ ...m, [slug]: r.data.variants }))).catch((e) => console.error("Variant load failed", e));
    });
  }, [q, lang]);

  if (!cart.length) return <div className="container-hw"><Seo title={t("your_cart")} noindex /><Empty title={t("cart_empty")} action={<Link to="/products" className="btn-primary" data-testid="cart-continue-shopping">{t("continue_shopping")}</Link>} /></div>;
  if (!q) return <Loading />;
  const errFor = (sku) => q.errors.find((e) => e.sku === sku);
  return (
    <div className="container-hw py-10" data-testid="cart-page">
      <Seo title={t("your_cart")} noindex />
      <h1 className="text-4xl sm:text-5xl mb-8">{t("your_cart")}</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-10">
        <ul className="divide-y divide-[color:var(--hw-line)] border-y border-[color:var(--hw-line)]">
          {q.lines.map((l) => {
            const e = errFor(l.sku);
            const vs = variantsBySlug[l.product_slug] || [];
            return (
              <li key={l.sku} className="py-6 flex gap-5" data-testid={`cart-line-${l.sku}`}>
                <Link to={`/p/${l.product_slug}`}><img src={l.image} alt={l.name} className="w-24 h-28 sm:w-32 sm:h-40 object-cover" /></Link>
                <div className="flex-1 space-y-2">
                  <div className="flex justify-between gap-3"><Link to={`/p/${l.product_slug}`} className="font-medium hover:underline">{l.name}</Link><span className="font-semibold" data-testid={`cart-line-total-${l.sku}`}>{chf(l.line_total)}</span></div>
                  <p className="text-xs text-[color:var(--hw-muted)]">{t("sku")}: {l.sku} · {chf(l.unit_price)}</p>
                  {vs.length > 1 && (
                    <select className="field h-9 max-w-xs text-xs" value={l.sku} onChange={(ev) => replaceSku(l.sku, ev.target.value)} aria-label={t("size")} data-testid={`cart-variant-select-${l.sku}`}>
                      {vs.map((x) => <option key={x.sku} value={x.sku} disabled={x.stock < 1}>{[x.size && val("s", x.size), x.color && val("c", x.color)].filter(Boolean).join(" · ")} – {chf(x.price)}</option>)}
                    </select>
                  )}
                  {e && <p className="text-xs text-[color:var(--hw-sale)]" role="alert" data-testid={`cart-line-error-${l.sku}`}>{e.code === "insufficient_stock" ? t("stock_issue", { n: e.available }) : t("unavailable")}</p>}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center border border-[color:var(--hw-line)] h-9 bg-white">
                      <button className="w-9 h-full grid place-items-center" aria-label="-" onClick={() => setQty(l.sku, l.qty - 1)} data-testid={`cart-qty-dec-${l.sku}`}><Minus size={13} /></button>
                      <span className="w-8 text-center text-sm" data-testid={`cart-qty-${l.sku}`}>{l.qty}</span>
                      <button className="w-9 h-full grid place-items-center" aria-label="+" onClick={() => setQty(l.sku, l.qty + 1)} data-testid={`cart-qty-inc-${l.sku}`}><Plus size={13} /></button>
                    </div>
                    <button className="text-xs flex items-center gap-1 hover:underline" onClick={() => setQty(l.sku, 0)} data-testid={`cart-remove-${l.sku}`}><Trash2 size={13} />{t("remove")}</button>
                  </div>
                </div>
              </li>
            );
          })}
          {q.errors.filter((e) => !q.lines.find((l) => l.sku === e.sku)).map((e) => <li key={e.sku} className="py-4 text-sm flex justify-between"><span>{e.sku} – {t("unavailable")}</span><button onClick={() => setQty(e.sku, 0)} className="link-u">{t("remove")}</button></li>)}
        </ul>
        <aside className="bg-white border border-[color:var(--hw-line)] p-6 h-fit lg:sticky lg:top-32 space-y-5" data-testid="cart-summary">
          <div>
            <p className="text-sm font-semibold mb-2">{t("shipping_method")}</p>
            {q.shipping_methods.map((m) => (
              <label key={m.id} className={`flex items-start gap-2 text-sm py-1.5 ${m.available ? "" : "opacity-50"}`}>
                <input type="radio" name="ship" disabled={!m.available} checked={q.shipping_method === m.id} onChange={() => setMethod(m.id)} data-testid={`cart-shipping-${m.id}`} className="mt-1" />
                <span className="flex-1">{m.name}<span className="block text-xs text-[color:var(--hw-muted)]">{m.available ? t("delivery_in", { a: m.days[0], b: m.days[1] }) : t("not_available_heavy")}</span></span><span>{chf(m.fee)}</span>
              </label>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); setApplied(coupon.trim().toUpperCase()); }} className="flex gap-2">
            <input className="field h-10" placeholder={t("coupon")} aria-label={t("coupon")} value={coupon} onChange={(e) => setCoupon(e.target.value)} data-testid="coupon-input" />
            <button className="btn-outline h-10" data-testid="coupon-apply-button">{t("apply_coupon")}</button>
          </form>
          {applied && q.coupon_error && <p className="text-xs text-[color:var(--hw-sale)]" role="alert" data-testid="coupon-error">{t(`coupon_${q.coupon_error}`)}</p>}
          <Totals q={q} />
          <Link to="/checkout" className={`btn-primary w-full h-12 ${q.errors.length ? "pointer-events-none opacity-50" : ""}`} aria-disabled={q.errors.length > 0} data-testid="cart-checkout-button">{t("checkout")}</Link>
          <Link to="/products" className="block text-center text-sm link-u">{t("continue_shopping")}</Link>
        </aside>
      </div>
      {sugg && <section className="mt-20"><SectionHead title={t("you_may_like")} /><ProductRail items={sugg.items.filter((p) => !q.lines.some((l) => l.product_slug === p.slug))} /></section>}
    </div>
  );
}
